import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  schema?: object;
}

export function useSEO({ title, description, keywords, canonical, schema }: SEOProps) {
  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    if (description) {
      let metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', description);
      }
    }

    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', keywords);
      }
    }

    // Update canonical
    if (canonical) {
      let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (linkCanonical) {
        linkCanonical.href = canonical;
      }
    }

    // Add or update Schema.org JSON-LD
    if (schema) {
      let existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.textContent = JSON.stringify(schema);
      } else {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      }
    }
  }, [title, description, keywords, canonical, schema]);
}

export function generateLocalBusinessSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "S2K Waste Services",
    "description": "Professional skip hire and waste management services in Kwa Zulu Natal",
    "url": `${siteUrl}`,
    "telephone": "+27844606965",
    "email": "info@s2kwaste.co.za",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Kwa Zulu Natal",
      "addressRegion": "KZN",
      "addressCountry": "ZA"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "-29.8587",
      "longitude": "31.0218"
    },
    "priceRange": "$$",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "07:00",
        "closes": "17:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "08:00",
        "closes": "13:00"
      }
    ],
    "areaServed": {
      "@type": "City",
      "name": "Kwa Zulu Natal"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Skip Hire Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Residential Skip Hire",
            "description": "Perfect for home renovations, garden clean-ups, and decluttering projects"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Commercial Skip Hire",
            "description": "Reliable solutions for businesses, offices, and retail spaces"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Construction & Industrial Waste",
            "description": "Heavy-duty skips for building sites and large-scale projects"
          }
        }
      ]
    }
  };
}

export function generateWebPageSchema(pageName: string) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": pageName,
    "url": siteUrl,
    "description": "S2K Waste Services - Professional skip hire and waste management in Johannesburg",
    "publisher": {
      "@type": "Organization",
      "name": "S2K Waste Services",
      "url": siteUrl
    }
  };
}

export function generateFAQSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What sizes of skip bins do you offer?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We offer a range of skip sizes including mini skip 2 & 3 ton bins, perfect for small to medium projects, as well as larger skips for construction and industrial waste."
        }
      },
      {
        "@type": "Question",
        "name": "How quickly can you deliver a skip?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We offer same-day delivery for residential skip hire in Johannesburg, subject to availability. Contact us for immediate booking."
        }
      },
      {
        "@type": "Question",
        "name": "What areas do you service?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We provide skip hire and waste management services throughout Johannesburg and surrounding areas in Gauteng."
        }
      },
      {
        "@type": "Question",
        "name": "What types of waste can I dispose of?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We accept most types of waste including household waste, garden waste, construction debris, and commercial waste. Hazardous materials require special handling."
        }
      }
    ]
  };
}
